import { addDoc, collection, doc, getDocs, getFirestore, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { obtenerAsientosPorEmpresa } from "./asientosService";
import { obtenerComprasPorEmpresa } from "./comprasService";
import { buildRceRecord, formatRceRecordToTxt, validateRceRecord } from "./rceModel";
import { buildRvieRecord, formatRvieRecordToTxt, validateRvieRecord } from "./rvieModel";
import { buildSunatFileName } from "./sunatFileNameService";
import { actualizarEstadoSirePeriodo, obtenerEstadoSirePeriodo, puedeGenerarArchivoSire } from "./sirePeriodoService";
import { SIRE_ESTADOS } from "./sireEstados";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const filtrarPorPeriodo = (fecha, periodo) => {
  if (!fecha || !periodo) return false;
  return String(fecha).slice(0, 7) === periodo;
};
const formatFecha = (f) => cleanText(f).replace(/-/g, "");
const normalizarPeriodo = (value) => cleanText(value).replace(/-/g, "").slice(0, 6);

const SCHEMA_SIRE = {
  RCE: 33,
  RVIE: 34,
};

export const validarEstructuraSire = ({ tipo, contenido }) => {
  const normalizedTipo = cleanText(tipo).toUpperCase();
  const columnasEsperadas = typeof SCHEMA_SIRE[normalizedTipo] === "number" ? SCHEMA_SIRE[normalizedTipo] : (Array.isArray(SCHEMA_SIRE[normalizedTipo]) ? SCHEMA_SIRE[normalizedTipo].length : 34);
  const rows = cleanText(contenido).split("\n").filter(Boolean);
  const invalidRows = rows
    .map((line, idx) => ({ idx: idx + 1, cols: line.split("|").length }))
    .filter((x) => x.cols !== columnasEsperadas)
    .slice(0, 20);
  return {
    ok: invalidRows.length === 0,
    columnasEsperadas,
    invalidRows,
  };
};

export const generateRVIEFileName = ({ ruc, periodo, extension = "txt" }) => {
  return buildSunatFileName({ ruc, tipo: "RVIE", periodo, extension });
};

export const generateRCEFileName = ({ ruc, periodo, extension = "txt" }) => {
  return buildSunatFileName({ ruc, tipo: "RCE", periodo, extension });
};

const validateBatchConsistency = ({ records = [], tipo, ruc, periodo }) => {
  const errors = [];
  const warnings = [];
  const uniqueIds = new Set();
  const expectedPeriodo = normalizarPeriodo(periodo);
  records.forEach((record, index) => {
    const recordRuc = cleanText(record.ruc || record.rucEmisor || record.documentoTributario?.ruc_emisor || "");
    const recordPeriodo = normalizarPeriodo(record.periodo || record.documentoTributario?.periodo || record.fechaEmision || "");
    if (recordRuc && cleanText(recordRuc) !== cleanText(ruc)) {
      errors.push({ field: "ruc", code: "MIXED_RUC", message: "No se puede generar un archivo mezclando distintos RUC.", lineNumber: index + 1 });
    }
    if (recordPeriodo && recordPeriodo !== expectedPeriodo) {
      errors.push({ field: "periodo", code: "MIXED_PERIOD", message: "No se puede generar un archivo mezclando distintos periodos.", lineNumber: index + 1 });
    }
    if (uniqueIds.has(record.id)) {
      warnings.push({ field: "id", code: "DUPLICATE_RECORD", message: "Se detectó un registro duplicado en el lote.", lineNumber: index + 1 });
    }
    uniqueIds.add(record.id);
  });
  return { valid: errors.length === 0, errors, warnings };
};

export const buildRvieFileContent = ({ records = [], ruc, periodo, extra = {} }) => {
  const batchValidation = validateBatchConsistency({ records, tipo: "RVIE", ruc, periodo });
  if (!batchValidation.valid) {
    return { valid: false, content: "", errors: batchValidation.errors, warnings: batchValidation.warnings, fileName: generateRVIEFileName({ ruc, periodo }) };
  }

  const lines = records.map((record) => {
    const validation = validateRvieRecord(record);
    if (!validation.valid) {
      return null;
    }
    return formatRvieRecordToTxt(record, { ruc, ...extra });
  }).filter(Boolean);

  return {
    valid: lines.length > 0 && batchValidation.valid,
    content: lines.join("\n"),
    errors: batchValidation.errors,
    warnings: batchValidation.warnings,
    fileName: generateRVIEFileName({ ruc, periodo }),
  };
};

export const buildRceFileContent = ({ records = [], ruc, periodo, extra = {} }) => {
  const batchValidation = validateBatchConsistency({ records, tipo: "RCE", ruc, periodo });
  if (!batchValidation.valid) {
    return { valid: false, content: "", errors: batchValidation.errors, warnings: batchValidation.warnings, fileName: generateRCEFileName({ ruc, periodo }) };
  }

  const lines = records.map((record) => {
    const validation = validateRceRecord(record);
    if (!validation.valid) {
      return null;
    }
    return formatRceRecordToTxt(record, { ruc, ...extra });
  }).filter(Boolean);

  return {
    valid: lines.length > 0 && batchValidation.valid,
    content: lines.join("\n"),
    errors: batchValidation.errors,
    warnings: batchValidation.warnings,
    fileName: generateRCEFileName({ ruc, periodo }),
  };
};

export const esCompraElegibleParaRce = (compra) => {
  if (!compra) return false;
  const estadoDocumento = cleanText(compra.estados?.documento).toUpperCase();
  const estadoCompra = cleanText(compra.estados?.compra).toUpperCase();

  if (estadoDocumento === "ANULADO" || estadoDocumento === "ANULADA") return false;
  if (estadoCompra === "ANULADO" || estadoCompra === "ANULADA") return false;

  return true;
};

export const prevalidarSireFile = ({ tipo, periodo, ruc, contenido, registros = [] }) => {
  const normalizedTipo = cleanText(tipo).toUpperCase();
  const columnasEsperadas = normalizedTipo === "RCE" ? 33 : 34;
  const errores = [];
  const warnings = [];

  const rows = cleanText(contenido).split("\n").filter(Boolean);

  let columnasValidas = true;
  rows.forEach((line, index) => {
    const cols = line.split("|");
    if (cols.length !== columnasEsperadas) {
      columnasValidas = false;
      errores.push({
        linea: index + 1,
        code: "INVALID_COLUMN_COUNT",
        message: `La línea ${index + 1} contiene ${cols.length} columnas (se esperaban ${columnasEsperadas}).`
      });
    }

    if (cols[0] && ruc && cleanText(cols[0]) !== cleanText(ruc)) {
      warnings.push({
        linea: index + 1,
        code: "RUC_MISMATCH",
        message: `La línea ${index + 1} contiene un RUC diferente al RUC de la sesión.`
      });
    }

    const expectedPeriodoTxt = cleanText(periodo).replace(/-/g, "").slice(0, 6) + "00";
    if (cols[2] && cols[2] !== expectedPeriodoTxt) {
      errores.push({
        linea: index + 1,
        code: "PERIOD_MISMATCH",
        message: `La línea ${index + 1} tiene periodo '${cols[2]}' pero se esperaba '${expectedPeriodoTxt}'.`
      });
    }

    if (cols[4] && !/^\d{2}\/\d{2}\/\d{4}$/.test(cols[4])) {
      errores.push({
        linea: index + 1,
        code: "INVALID_DATE_FORMAT",
        message: `La línea ${index + 1} tiene formato de fecha de emisión inválido '${cols[4]}'.`
      });
    }

    const lastColIndex = columnasEsperadas - 1;
    if (cols[lastColIndex] !== "1") {
      errores.push({
        linea: index + 1,
        code: "INVALID_ANNOTATION_STATE",
        message: `La línea ${index + 1} tiene estado de anotación '${cols[lastColIndex]}' en lugar de '1'.`
      });
    }
  });

  return {
    valido: errores.length === 0 && columnasValidas && rows.length > 0,
    tipo: normalizedTipo,
    periodo: cleanText(periodo),
    registros: rows.length,
    errores,
    warnings,
    columnasEsperadas,
    columnasValidas
  };
};

export const generarRceTxt = async ({ rucEmpresa, periodo, bypassStateCheck = false }) => {
  if (!bypassStateCheck) {
    const stPeriodo = await obtenerEstadoSirePeriodo({ rucEmpresa, periodo, tipo: "RCE" });
    if (!puedeGenerarArchivoSire(stPeriodo)) {
      throw new Error("El período no está habilitado para generación SIRE.");
    }
  }

  const compras = await obtenerComprasPorEmpresa(rucEmpresa);
  const comprasElegibles = compras.filter((c) => {
    const fecha = cleanText(c.comprobante?.fecha_emision || c.fecha_emision || "");
    return esCompraElegibleParaRce(c) && filtrarPorPeriodo(fecha, periodo);
  });

  const registros = comprasElegibles.map((c) => buildRceRecord({
    documentoTributario: {
      id: c.id,
      ruc_emisor: cleanText(rucEmpresa),
      tipo_comprobante: cleanText(c.comprobante?.tipo || c.tipo || ""),
      serie: cleanText(c.comprobante?.serie || c.serie || ""),
      numero: cleanText(c.comprobante?.numero || c.numero || ""),
      fecha_emision: cleanText(c.comprobante?.fecha_emision || c.fecha_emision || ""),
      fecha_vencimiento: cleanText(c.comprobante?.fecha_vencimiento || c.fecha_vencimiento || c.comprobante?.fecha_emision || c.fecha_emision || ""),
      periodo: cleanText(periodo),
      moneda: cleanText(c.comprobante?.moneda || c.moneda || "PEN"),
      tipo_cambio: toNum(c.comprobante?.tipo_cambio || c.tipo_cambio || 1),
      tercero: {
        tipo_documento: cleanText(c.proveedor_tipo_documento || (cleanText(c.proveedor_ruc || c.proveedorRuc).length === 11 ? "RUC" : "DNI")),
        numero_documento: cleanText(c.proveedor_ruc || c.proveedorRuc),
        razon_social: cleanText(c.proveedor_razon_social || c.proveedorRazonSocial),
      },
      importes: {
        subtotal: toNum(c.montos?.subtotal ?? c.subtotal),
        igv: toNum(c.montos?.igv ?? c.igv),
        total: toNum(c.montos?.total ?? c.total),
      },
      tipo_operacion: cleanText(c.tipo_operacion || "GRAVADO"),
      estado_sunat: cleanText(c.sunat?.estado === "VALIDO" ? "VALIDO" : "PENDIENTE"),
    },
    registroTributario: { id: `reg-${c.id}` },
  }));

  const filePayload = buildRceFileContent({ records: registros, ruc: rucEmpresa, periodo });
  const prevalidacion = prevalidarSireFile({
    tipo: "RCE",
    periodo,
    ruc: rucEmpresa,
    contenido: filePayload.content,
    registros
  });

  return {
    tipo: "RCE",
    periodo,
    totalRegistros: registros.length,
    totalRegisters: registros.length,
    total_registros: registros.length,
    contenido: filePayload.content,
    estructura: { ok: prevalidacion.valido, columnasEsperadas: 33, invalidRows: prevalidacion.errores },
    prevalidacion,
    columnas: 33,
    fileName: generateRCEFileName({ ruc: rucEmpresa, periodo }),
  };
};

export const generarRvieTxt = async ({ rucEmpresa, periodo, bypassStateCheck = false }) => {
  if (!bypassStateCheck) {
    const stPeriodo = await obtenerEstadoSirePeriodo({ rucEmpresa, periodo, tipo: "RVIE" });
    if (!puedeGenerarArchivoSire(stPeriodo)) {
      throw new Error("El período no está habilitado para generación SIRE.");
    }
  }

  const cleanRuc = cleanText(rucEmpresa);
  const docsSnap = await getDocs(
    query(
      collection(db, CONT_COLLECTIONS.DOCUMENTOS_TRIBUTARIOS),
      where("ruc_emisor", "==", cleanRuc)
    )
  );
  const compSnap = await getDocs(
    query(
      collection(db, CONT_COLLECTIONS.COMPROBANTES_VENTA),
      where("ruc_empresa", "==", cleanRuc)
    )
  );

  let docsTrib = docsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => {
    const f = cleanText(d.fecha_emision || d.fecha || "");
    const idStr = String(d.id || "");
    const origTipo = cleanText(d.origen?.mapValue?.fields?.tipo?.stringValue || d.origen?.tipo || d.tipo || "").toUpperCase();
    const emisorRuc = cleanText(d.ruc_emisor || d.ruc_empresa || d.empresa_id || "");

    // Excluir compras explícitas
    if (idStr.startsWith("doc_trib_c_") || origTipo === "COMPRA" || (emisorRuc && emisorRuc !== cleanRuc)) {
      return false;
    }

    const esVenta = idStr.startsWith("doc_trib_v_") || origTipo === "VENTA" || emisorRuc === cleanRuc;
    return esVenta && filtrarPorPeriodo(f, periodo);
  });

  const compDocs = compSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => {
    const f = cleanText(d.fecha_emision || d.fecha || "");
    const ruc = cleanText(d.ruc_emisor || d.ruc_empresa || "");
    if (ruc && ruc !== cleanRuc) return false;

    const fechaEsPeriodo = filtrarPorPeriodo(f, periodo);
    const tieneNc = Boolean(d.tiene_nc || d.nota_credito_referencia || d.nota_credito_id);
    return fechaEsPeriodo || tieneNc;
  });

  // Deduplicar documentos de venta locales por Serie y Número
  const mapVentas = new Map();
  docsTrib.forEach((d) => {
    const s = cleanText(d.serie || "E001");
    const n = cleanText(d.numero || "");
    const key = `${s}-${cleanText(n).replace(/^0+/, "")}`;
    if (!mapVentas.has(key) || d.id.startsWith("doc_trib_v_")) {
      mapVentas.set(key, d);
    }
  });

  compDocs.forEach((c) => {
    const s = cleanText(c.serie || "E001");
    const n = cleanText(c.numero || "");
    const f = cleanText(c.fecha_emision || c.fecha || "");
    const key = `${s}-${cleanText(n).replace(/^0+/, "")}`;

    if (filtrarPorPeriodo(f, periodo) && !mapVentas.has(key)) {
      const rawTipo = cleanText(c.tipo || "01").toUpperCase();
      const tipoCompNormalized = rawTipo === "NOTA_CREDITO" || rawTipo === "07" ? "07" : rawTipo === "BOLETA" || rawTipo === "03" ? "03" : "01";
      mapVentas.set(key, {
        id: c.id,
        ruc_emisor: cleanRuc,
        tipo_comprobante: tipoCompNormalized,
        serie: s,
        numero: n,
        fecha_emision: f,
        periodo: cleanText(periodo),
        moneda: cleanText(c.moneda || "PEN"),
        tipo_cambio: toNum(c.tipo_cambio || 1),
        tercero: {
          tipo_documento: "6",
          numero_documento: cleanText(c.cliente_ruc || ""),
          razon_social: cleanText(c.cliente_razon_social || ""),
        },
        importes: {
          subtotal: toNum(c.subtotal),
          igv: toNum(c.igv),
          total: toNum(c.total),
        },
        tipo_operacion: "GRAVADO",
        estado_sunat: "PENDIENTE",
      });
    }

    // Extraer Nota de Crédito si el comprobante fue anulado con NC
    const tieneNc = Boolean(c.tiene_nc || c.nota_credito_referencia || c.nota_credito_id);
    if (tieneNc) {
      const ncRef = cleanText(c.nota_credito_referencia || c.nota_credito_id || "");
      const parts = ncRef.split("-");
      const serieRaw = cleanText(parts[0] || "E001").toUpperCase();
      const serieNc = serieRaw.replace(/^FC/, "E").replace(/^F/, "E") || "E001";
      const numeroNc = cleanText(parts[1] || "").replace(/^0+/, "");

      if (serieNc && numeroNc) {
        const keyNc = `${serieNc}-${numeroNc}`;
        if (!mapVentas.has(keyNc)) {
          const tot = toNum(c.total);
          const sub = toNum(c.subtotal);
          const igv = toNum(c.igv);

          let fechaNc = `${periodo}-07`;
          if (typeof c.fecha_anulacion_nc === "string" && c.fecha_anulacion_nc.length >= 10) {
            fechaNc = c.fecha_anulacion_nc.slice(0, 10);
          }

          mapVentas.set(keyNc, {
            id: `nc_${c.id}`,
            documentoTributarioId: `nc_${c.id}`,
            ruc_emisor: cleanRuc,
            tipo_comprobante: "07",
            serie: serieNc,
            numero: numeroNc,
            fecha_emision: fechaNc,
            fecha_vencimiento: fechaNc,
            periodo: cleanText(periodo),
            moneda: cleanText(c.moneda || "PEN"),
            tipo_cambio: toNum(c.tipo_cambio || 1),
            tercero: {
              tipo_documento: "6",
              numero_documento: cleanText(c.cliente_ruc || ""),
              razon_social: cleanText(c.cliente_razon_social || ""),
            },
            importes: {
              base_gravada: -Math.abs(sub),
              subtotal: -Math.abs(sub),
              igv: -Math.abs(igv),
              total: -Math.abs(tot),
            },
            documento_relacionado: {
              tipo_documento_referencia: "01",
              serie_referencia: s,
              numero_referencia: n,
            },
            tipo_operacion: "GRAVADO",
            estado_sunat: "PENDIENTE",
          });
        }
      }
    }
  });

  docsTrib = Array.from(mapVentas.values());

  // Si no hay suficientes documentos tributarios, consultar asientos contables
  if (docsTrib.length === 0) {
    const asientos = await obtenerAsientosPorEmpresa(cleanRuc);
    const ventasAsientos = asientos.filter(
      (a) => filtrarPorPeriodo(a.fecha, periodo) && (a.origen_tipo === "VENTA" || a.origen === "VENTA" || (a.haber || []).some((x) => String(x.cuenta || "").startsWith("7")))
    );
    docsTrib = ventasAsientos.map((a) => ({
      id: a.id,
      ruc_emisor: cleanRuc,
      tipo_comprobante: "01",
      serie: cleanText(a.serie || "E001"),
      numero: cleanText(a.numero || a.id.split("_").pop() || "1"),
      fecha_emision: cleanText(a.fecha || ""),
      periodo: cleanText(periodo),
      moneda: cleanText(a.moneda || "PEN"),
      tipo_cambio: toNum(a.tipo_cambio || 1),
      tercero: {
        tipo_documento: "6",
        numero_documento: cleanText(a.cliente_ruc || a.cliente_numero_documento || ""),
        razon_social: cleanText(a.cliente_razon_social || a.glosa || ""),
      },
      importes: {
        subtotal: toNum(a.subtotal || a.total_debe),
        igv: toNum(a.igv || 0),
        total: toNum(a.total || a.total_haber || a.total_debe),
      },
      tipo_operacion: "GRAVADO",
      estado_sunat: "PENDIENTE",
    }));
  }

  const registros = docsTrib.map((d) => {
    const terceroObj = d.tercero?.mapValue?.fields ? {
      tipo_documento: cleanText(d.tercero.mapValue.fields.tipo_documento?.stringValue === "RUC" ? "6" : d.tercero.mapValue.fields.tipo_documento?.stringValue || "6"),
      numero_documento: cleanText(d.tercero.mapValue.fields.numero_documento?.stringValue || ""),
      razon_social: cleanText(d.tercero.mapValue.fields.razon_social?.stringValue || ""),
    } : {
      tipo_documento: cleanText(d.tercero?.tipo_documento === "RUC" ? "6" : d.tercero?.tipo_documento || "6"),
      numero_documento: cleanText(d.tercero?.numero_documento || d.cliente_ruc || ""),
      razon_social: cleanText(d.tercero?.razon_social || d.cliente_razon_social || ""),
    };

    const importesObj = d.importes?.mapValue?.fields ? {
      subtotal: toNum(d.importes.mapValue.fields.base_gravada?.integerValue || d.importes.mapValue.fields.base_gravada?.doubleValue || d.importes.mapValue.fields.subtotal?.doubleValue || 0),
      igv: toNum(d.importes.mapValue.fields.igv?.doubleValue || 0),
      total: toNum(d.importes.mapValue.fields.total?.doubleValue || 0),
    } : {
      subtotal: toNum(d.importes?.base_gravada ?? d.importes?.subtotal ?? d.subtotal),
      igv: toNum(d.importes?.igv ?? d.igv),
      total: toNum(d.importes?.total ?? d.total),
    };

    const rawTipo = cleanText(d.tipo_comprobante || "").toUpperCase();
    const tipoCompNormalized = rawTipo === "07" || rawTipo === "NOTA_CREDITO" ? "07" : (rawTipo === "03" || rawTipo === "BOLETA" ? "03" : "01");

    return buildRvieRecord({
      documentoTributario: {
        id: d.id,
        ruc_emisor: cleanRuc,
        tipo_comprobante: tipoCompNormalized,
        serie: cleanText(d.serie || "E001"),
        numero: cleanText(d.numero || ""),
        fecha_emision: cleanText(d.fecha_emision || d.fecha || ""),
        periodo: cleanText(periodo),
        moneda: cleanText(d.moneda || "PEN"),
        tipo_cambio: toNum(d.tipo_cambio || 1),
        tercero: terceroObj,
        importes: importesObj,
        tipo_operacion: cleanText(d.tipo_operacion || "GRAVADO"),
        estado_sunat: cleanText(d.estado_sunat || "PENDIENTE"),
      },
      registroTributario: { id: `reg-${d.id}` },
    });
  });

  const filePayload = buildRvieFileContent({ records: registros, ruc: rucEmpresa, periodo });
  const prevalidacion = prevalidarSireFile({
    tipo: "RVIE",
    periodo,
    ruc: rucEmpresa,
    contenido: filePayload.content,
    registros
  });

  return {
    tipo: "RVIE",
    periodo,
    totalRegistros: registros.length,
    totalRegisters: registros.length,
    total_registros: registros.length,
    contenido: filePayload.content,
    estructura: { ok: prevalidacion.valido, columnasEsperadas: 34, invalidRows: prevalidacion.errores },
    prevalidacion,
    columnas: 34,
    fileName: generateRVIEFileName({ ruc: rucEmpresa, periodo }),
  };
};

export const guardarGeneradoSire = async ({ rucEmpresa, payload, usuario }) => {
  const ref = await addDoc(collection(db, CONT_COLLECTIONS.SIRE_ENVIOS), {
    ruc_empresa: cleanText(rucEmpresa),
    tipo: cleanText(payload.tipo),
    periodo: cleanText(payload.periodo),
    total_registros: toNum(payload.totalRegistros),
    contenido: cleanText(payload.contenido),
    estado: "GENERADO",
    estructura_ok: Boolean(payload?.estructura?.ok),
    columnas_esperadas: toNum(payload?.estructura?.columnasEsperadas),
    invalid_rows: payload?.estructura?.invalidRows || [],
    usuario: cleanText(usuario),
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return ref.id;
};

export const obtenerEnviosSire = async (rucEmpresa) => {
  const q = query(collection(db, CONT_COLLECTIONS.SIRE_ENVIOS), where("ruc_empresa", "==", cleanText(rucEmpresa)));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.periodo || "").localeCompare(String(a.periodo || "")));
};

export const guardarPropuestaSunat = async ({ rucEmpresa, periodo, tipo, registros = [], fuente = "SUNAT_API", contenido = "" }) => {
  const cleanRuc = cleanText(rucEmpresa);
  const cleanPer = cleanText(periodo);
  const cleanTip = cleanText(tipo).toUpperCase();
  const docId = `${cleanRuc}_${cleanPer}_${cleanTip}`;

  const docRef = doc(db, CONT_COLLECTIONS.SIRE_PROPUESTAS, docId);

  const dataPayload = {
    ruc_empresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    registros,
    total_registros: registros.length,
    fuente: cleanText(fuente),
    contenido: cleanText(contenido),
    updated_at: serverTimestamp(),
  };

  await setDoc(docRef, dataPayload, { merge: true });

  // Regla 10: Toda nueva propuesta SUNAT descargada/actualizada reinicia el estado a PROPUESTA_DESCARGADA
  try {
    await actualizarEstadoSirePeriodo({
      rucEmpresa: cleanRuc,
      periodo: cleanPer,
      tipo: cleanTip,
      nuevoEstado: SIRE_ESTADOS.PROPUESTA_DESCARGADA,
      metadata: {
        total_sunat: registros.length,
        propuesta_origen: cleanText(fuente),
      },
    });
  } catch (e) {
    // Ignorar si la transición fue desde un estado de origen sin cambiar
  }

  return docId;
};

export const obtenerPropuestaSunat = async ({ rucEmpresa, periodo, tipo }) => {
  const q = query(
    collection(db, CONT_COLLECTIONS.SIRE_PROPUESTAS),
    where("ruc_empresa", "==", cleanText(rucEmpresa)),
    where("periodo", "==", cleanText(periodo)),
    where("tipo", "==", cleanText(tipo).toUpperCase())
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const compararConPropuestaSunat = async ({ rucEmpresa, periodo, tipo, generado }) => {
  const propuesta = await obtenerPropuestaSunat({ rucEmpresa, periodo, tipo });
  if (!propuesta) {
    return { encontrado: false, diferencias: [], resumen: "No existe propuesta SUNAT en cont_sire_propuestas para comparar." };
  }

  const setLocal = new Set(cleanText(generado).split("\n").filter(Boolean));
  const setSunat = new Set(cleanText(propuesta.contenido).split("\n").filter(Boolean));
  const faltantes = [...setSunat].filter((x) => !setLocal.has(x)).slice(0, 20);
  const sobrantes = [...setLocal].filter((x) => !setSunat.has(x)).slice(0, 20);

  return {
    encontrado: true,
    diferencias: { faltantes, sobrantes },
    resumen: `Comparación ${tipo} ${periodo}: faltantes ${faltantes.length}, sobrantes ${sobrantes.length}.`,
  };
};

export const guardarConfiguracionSire = async ({ rucEmpresa, usuarioSol, claveSol, clientId, clientSecret, entorno = "PRODUCCION" }) => {
  const cleanRuc = cleanText(rucEmpresa);
  if (!cleanRuc) throw new Error("RUC de empresa no válido");

  const docRef = doc(db, CONT_COLLECTIONS.SIRE_CONFIG, cleanRuc);
  const tieneApi = Boolean(cleanText(clientId) && cleanText(clientSecret) && cleanText(usuarioSol) && cleanText(claveSol));

  const payload = {
    ruc_empresa: cleanRuc,
    sunat: {
      estado: tieneApi ? "CONECTADO" : "SIN_CONFIGURAR",
      entorno: cleanText(entorno || "PRODUCCION"),
      usuario_sol_configurado: Boolean(cleanText(usuarioSol)),
      api_configurada: tieneApi,
      usuario_sol_masked: cleanText(usuarioSol) ? `${cleanText(usuarioSol).slice(0, 3)}***` : "",
      client_id_masked: cleanText(clientId) ? `${cleanText(clientId).slice(0, 4)}***` : "",
      ultima_actualizacion: serverTimestamp(),
    },
    updated_at: serverTimestamp(),
  };

  await setDoc(docRef, payload, { merge: true });
  return { success: true, apiConfigurada: tieneApi, estado: tieneApi ? "CONECTADO" : "SIN_CONFIGURAR" };
};

export const obtenerConfiguracionSire = async (rucEmpresa) => {
  const cleanRuc = cleanText(rucEmpresa);
  if (!cleanRuc) return null;

  const docRef = doc(db, CONT_COLLECTIONS.SIRE_CONFIG, cleanRuc);
  const snap = await getDocs(query(collection(db, CONT_COLLECTIONS.SIRE_CONFIG), where("ruc_empresa", "==", cleanRuc)));

  if (snap.empty) {
    return {
      ruc_empresa: cleanRuc,
      sunat: {
        estado: "SIN_CONFIGURAR",
        entorno: "PRODUCCION",
        usuario_sol_configurado: false,
        api_configurada: false,
      },
    };
  }

  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};
